import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Filters } from '../../pages/Dashboard/Filters';
import { useSelector } from 'react-redux';
import { formatNumber } from '../../utils';
import { getAverageDeveloperCostFromSource } from '../../utils/companyConfigurations';
import { sendRequest } from '../../utils/dateHelpers';
import Loading from '../Loading';
import DisplayModeToggle from '../DisplayModeToggle';

const DISPLAY_MODES = {
  REAL_EFFORT: 'git_effort',
  INVESTMENT: 'investment'
};

const COMMITS_REQUEST_PAGE_SIZE = 50;

const TIMESTAMP_FIELDS = [
  'timestamp',
  'commit_timestamp',
  'commit_date',
  'date',
  'created_at',
  'createdAt',
  'updated_at',
  'updatedAt',
  'author_date',
  'authorDate',
  'committed_date',
  'committedDate'
];

const EMPTY_SUMMARY = {
  totalGitEffort: 0,
  totalAiEffort: 0,
  totalDevEffort: 0,
  totalInvestment: 0,
  totalAiInvestment: 0,
  totalDevInvestment: 0
};

const convertEffortToInvestment = (value, averageCost) =>
  (Number(value || 0) / 60) * averageCost;

const normalizeEntityNames = value => {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeEntityNames).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue ? [trimmedValue] : [];
  }

  if (value && typeof value === 'object') {
    return normalizeEntityNames(
      value.label ||
        value.name ||
        value.project ||
        value.repo ||
        value.title ||
        value.value ||
        ''
    );
  }

  return [];
};

const normalizeEntityIds = value => {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeEntityIds).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue ? [trimmedValue] : [];
  }

  if (value && typeof value === 'object') {
    return normalizeEntityIds(value.uid || value.id || value.value || '');
  }

  return [];
};

const getEntityLabel = entity =>
  entity?.label || entity?.name || entity?.project || entity?.repo || '';

const getUniqueEntityNames = values => [
  ...new Set(values.flatMap(normalizeEntityNames).filter(Boolean))
];

const getCommitTimestamp = commit => {
  for (const field of TIMESTAMP_FIELDS) {
    const rawValue = commit?.[field];

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      continue;
    }

    const numericValue = Number(rawValue);
    if (!Number.isNaN(numericValue)) {
      return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    }

    const parsedDate = Date.parse(rawValue);
    if (!Number.isNaN(parsedDate)) {
      return parsedDate;
    }
  }

  return 0;
};

const sortCommitsByTimestampDesc = commits =>
  [...commits].sort((left, right) => {
    const timestampDifference =
      getCommitTimestamp(right) - getCommitTimestamp(left);

    if (timestampDifference !== 0) {
      return timestampDifference;
    }

    const leftId = String(left?.id || left?._id || '');
    const rightId = String(right?.id || right?._id || '');
    return rightId.localeCompare(leftId);
  });

const formatCommitTimestamp = commit => {
  const timestamp = getCommitTimestamp(commit);

  if (!timestamp) {
    return '-';
  }

  return new Intl.DateTimeFormat('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
};

const UserDetail = () => {
  const { userInfo = {} } = useSelector(state => state);
  const [filters, setFilters] = useState({});
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.REAL_EFFORT);
  const [openCommitId, setOpenCommitId] = useState(null);
  const sentinelRef = useRef(null);
  const latestRequestRef = useRef(0);
  const cursorRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  const companyUid = userInfo?.user?.companyId;
  const roleBasedAverageDeveloperCost =
    getAverageDeveloperCostFromSource(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : getAverageDeveloperCostFromSource(userInfo?.user);
  const reposMap = userInfo?.dashboardData?.repos || {};
  const projectsMap = userInfo?.dashboardData?.projects || {};
  const hasClientSideInvestmentRate = averageDeveloperCost > 0;
  const isInvestment = displayMode === DISPLAY_MODES.INVESTMENT;

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchInput]);

  const loadCommits = useCallback(
    async ({
      append,
      searchTerm = debouncedSearch,
      filtersSnapshot = filters
    }) => {
      if (!companyUid || !Object.keys(filtersSnapshot || {}).length) {
        return;
      }

      if (append && (isLoadingMoreRef.current || !cursorRef.current)) {
        return;
      }

      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const requestBody = {
          limit: COMMITS_REQUEST_PAGE_SIZE,
          filters: {
            ...filtersSnapshot,
            search: searchTerm.trim(),
            operation: DISPLAY_MODES.REAL_EFFORT
          }
        };

        if (append && cursorRef.current) {
          requestBody.cursor = cursorRef.current;
        }

        const response = await sendRequest(
          '/table-detail/data',
          'POST',
          requestBody
        );

        if (requestId !== latestRequestRef.current) {
          return;
        }

        const responseData = Array.isArray(response?.data) ? response.data : [];
        const sortedResponseData = sortCommitsByTimestampDesc(responseData);

        setCommits(currentCommits =>
          append
            ? [...currentCommits, ...sortedResponseData]
            : sortedResponseData
        );
        setCursor(response?.pagination?.nextCursor || null);
        setHasMore(Boolean(response?.pagination?.hasMore));
        setSummary(response?.summary || EMPTY_SUMMARY);

        if (!append) {
          setOpenCommitId(null);
        }
      } catch (requestError) {
        const message =
          requestError?.errorData?.error ||
          requestError?.message ||
          'Failed to load commit details.';

        setError(message);

        if (!append) {
          setCommits([]);
          setCursor(null);
          setHasMore(false);
          setSummary(EMPTY_SUMMARY);
          setOpenCommitId(null);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [companyUid, debouncedSearch, filters]
  );

  useEffect(() => {
    if (!Object.keys(filters)?.length || !companyUid) {
      setCommits([]);
      setCursor(null);
      setHasMore(false);
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }

    setCommits([]);
    setCursor(null);
    setHasMore(false);
    setSummary(EMPTY_SUMMARY);
    setOpenCommitId(null);
    loadCommits({
      append: false,
      searchTerm: debouncedSearch,
      filtersSnapshot: filters
    });
  }, [companyUid, debouncedSearch, filters, loadCommits]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;

        if (!entry?.isIntersecting || !hasMore || loading || isLoadingMore) {
          return;
        }

        loadCommits({
          append: true,
          searchTerm: debouncedSearch,
          filtersSnapshot: filters
        });
      },
      { rootMargin: '250px' }
    );

    const currentRef = sentinelRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [debouncedSearch, filters, hasMore, isLoadingMore, loadCommits, loading]);

  const handleFilters = filtersTemp => {
    setFilters(filtersTemp);
  };

  const totalGitEffort = summary.totalGitEffort || 0;
  const totalAiEffort = summary.totalAiEffort || 0;
  const totalDevEffort = summary.totalDevEffort || 0;
  const displayTotalEffort = isInvestment
    ? hasClientSideInvestmentRate
      ? convertEffortToInvestment(totalGitEffort, averageDeveloperCost)
      : Number(summary.totalInvestment || 0)
    : totalGitEffort;
  const displayTotalAiEffort = isInvestment
    ? hasClientSideInvestmentRate
      ? convertEffortToInvestment(totalAiEffort, averageDeveloperCost)
      : Number(summary.totalAiInvestment || 0)
    : totalAiEffort;
  const displayTotalDevEffort = isInvestment
    ? hasClientSideInvestmentRate
      ? convertEffortToInvestment(totalDevEffort, averageDeveloperCost)
      : Number(summary.totalDevInvestment || 0)
    : totalDevEffort;
  const unitPrefix = isInvestment ? '$' : '';
  const totalEffortTitle = isInvestment
    ? 'Estimated Cost Without AI ($)'
    : 'Baseline Effort (REV)';
  const aiEffortTitle = isInvestment
    ? 'Estimated Cost Avoided ($)'
    : 'Effort Saved by AI';
  const devEffortTitle = isInvestment
    ? 'Estimated Human Cost Required ($)'
    : 'Human Effort Required';

  const categoryLabelMap = useMemo(
    () => ({
      Feature_Addition: 'feature',
      Bug_Fixes: 'bugfix',
      Refactoring: 'refactor',
      Build_System_Changes: 'build-system',
      Performance_Improvements: 'performance',
      Security_Fixes: 'security',
      Dependency_Updates: 'dependency',
      Test_Additions_Changes: 'test',
      Configuration_Changes: 'configuration',
      Documentation_Updates: 'documentation',
      Experimental_Features: 'experimental',
      Style_Changes: 'style',
      Other: 'other'
    }),
    []
  );

  const repoProjectLookup = useMemo(() => {
    const lookup = {};

    Object.entries(reposMap).forEach(([repoUid, repo]) => {
      const repoProjectNames = normalizeEntityNames(repo?.project);

      if (repoProjectNames.length) {
        lookup[repoUid] = [...new Set(repoProjectNames)];
      }
    });

    Object.entries(projectsMap).forEach(([projectUid, project]) => {
      const projectName = getEntityLabel(project) || projectUid;
      const projectRepos = Array.isArray(project?.repos) ? project.repos : [];

      projectRepos.forEach(projectRepo => {
        const repoIds = normalizeEntityIds(projectRepo);

        repoIds.forEach(repoUid => {
          if (!lookup[repoUid]) {
            lookup[repoUid] = [];
          }

          if (!lookup[repoUid].includes(projectName)) {
            lookup[repoUid].push(projectName);
          }
        });
      });
    });

    return lookup;
  }, [projectsMap, reposMap]);

  const getHashtags = commit =>
    Object.entries(categoryLabelMap)
      .filter(([key]) => Number(commit?.[key]) > 0)
      .map(([, label]) => `#${label}`);

  const getCommitSha = commit =>
    commit?.sha || commit?.commit_hash || commit?.commit_sha || '-';

  const getShortCommitSha = commit => {
    const sha = getCommitSha(commit);
    return sha === '-' ? sha : sha.slice(0, 6);
  };

  const getCommitAuthor = commit =>
    commit?.author?.name ||
    commit?.author_name ||
    commit?.author ||
    commit?.developer ||
    '-';

  const getCommitRepo = commit => {
    const repoNames = getUniqueEntityNames([
      commit?.repo_name,
      commit?.repo,
      ...normalizeEntityIds(commit?.repo_uid).map(
        repoUid => reposMap?.[repoUid]
      )
    ]);

    return repoNames[0] || normalizeEntityIds(commit?.repo_uid)[0] || '-';
  };

  const getCommitProjects = commit => {
    const projectNames = getUniqueEntityNames([
      commit?.project_name,
      commit?.project,
      ...normalizeEntityIds(commit?.project_uid).map(
        projectUid => projectsMap?.[projectUid]
      ),
      ...normalizeEntityIds(commit?.repo_uid).flatMap(
        repoUid => repoProjectLookup?.[repoUid] || []
      )
    ]);

    return projectNames.length ? projectNames.join(', ') : '-';
  };

  const getCommitRev = commit => Number(commit?.rev ?? commit?.git_effort ?? 0);

  const getCommitValue = commit => {
    if (hasClientSideInvestmentRate) {
      return convertEffortToInvestment(
        getCommitRev(commit),
        averageDeveloperCost
      );
    }

    const numericValue = Number(commit?.value);

    if (Number.isFinite(numericValue)) {
      return numericValue;
    }

    return 0;
  };

  const toggleCommitDetails = commitId => {
    setOpenCommitId(current => (current === commitId ? null : commitId));
  };

  if (!userInfo?.user) {
    return <Loading type="card" />;
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3 pb-3">
        <Filters
          userInfo={userInfo}
          onChange={handleFilters}
          isHidden={['viewType', 'operation']}
        />
        <DisplayModeToggle value={displayMode} onChange={setDisplayMode} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5 mb-6">
        {loading ? (
          <>
            {[1, 2, 3].map(key => (
              <div
                key={key}
                className="relative overflow-hidden rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark animate-pulse"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="relative overflow-hidden rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-2xl font-bold text-black dark:text-white mb-1">
                    {unitPrefix}
                    {displayTotalEffort.toLocaleString('en-US', {
                      maximumFractionDigits: 0
                    })}
                  </h4>
                  <span className="text-sm font-medium text-success">
                    {totalEffortTitle}
                  </span>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-600 shadow-lg">
                  <svg
                    className="fill-white"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                      fill=""
                    />
                    <path d="M11 7H13V13H11V7ZM11 15H13V17H11V15Z" fill="" />
                  </svg>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-400 to-green-600"></div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-2xl font-bold text-black dark:text-white mb-1">
                    {unitPrefix}
                    {displayTotalAiEffort.toLocaleString('en-US', {
                      maximumFractionDigits: 0
                    })}
                    <span className="ml-2 text-sm font-medium text-meta-5">
                      (
                      {totalGitEffort > 0
                        ? Math.round((totalAiEffort / totalGitEffort) * 100)
                        : '0'}
                      %)
                    </span>
                  </h4>
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {aiEffortTitle}
                  </span>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg">
                  <svg
                    className="fill-white"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 9V7C20 5.9 19.1 5 18 5H6C4.9 5 4 5.9 4 7V9C2.9 9 2 9.9 2 11V13C2 14.1 2.9 15 4 15V17C4 18.1 4.9 19 6 19H18C19.1 19 20 18.1 20 17V15C21.1 15 22 14.1 22 13V11C22 9.9 21.1 9 20 9ZM18 17H6V7H18V17Z"
                      fill=""
                    />
                    <path
                      d="M9 10C8.45 10 8 10.45 8 11C8 11.55 8.45 12 9 12C9.55 12 10 11.55 10 11C10 10.45 9.55 10 9 10Z"
                      fill=""
                    />
                    <path
                      d="M15 10C14.45 10 14 10.45 14 11C14 11.55 14.45 12 15 12C15.55 12 16 11.55 16 11C16 10.45 15.55 10 15 10Z"
                      fill=""
                    />
                  </svg>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-purple-500 to-indigo-600"></div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-2xl font-bold text-black dark:text-white mb-1">
                    {unitPrefix}
                    {displayTotalDevEffort.toLocaleString('en-US', {
                      maximumFractionDigits: 0
                    })}
                    <span className="ml-2 text-sm font-medium text-primary">
                      (
                      {totalGitEffort > 0
                        ? Math.round((totalDevEffort / totalGitEffort) * 100)
                        : '0'}
                      %)
                    </span>
                  </h4>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {devEffortTitle}
                  </span>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg">
                  <svg
                    className="fill-white"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                      fill=""
                    />
                  </svg>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
            </div>
          </>
        )}
      </div>

      <div className="mb-4">
        <div className="rounded-xl border border-stroke bg-white px-4 py-3 shadow-default dark:border-strokedark dark:bg-boxdark">
          <input
            type="text"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Search commits"
            className="w-full bg-transparent text-sm text-black outline-none placeholder:text-slate-400 dark:text-white"
          />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {loading
          ? null
          : commits.map((commit, index) => {
              const commitId =
                commit.id ||
                commit._id ||
                commit.sha ||
                commit.commit_hash ||
                index;
              const isOpen = openCommitId === commitId;
              const hashtags = getHashtags(commit);

              return (
                <div
                  key={commitId}
                  className="overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
                >
                  <button
                    type="button"
                    className="w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-meta-4 sm:px-6"
                    onClick={() => toggleCommitDetails(commitId)}
                    aria-expanded={isOpen}
                  >
                    <div className="min-w-0">
                      <div className="overflow-x-auto">
                        <div className="grid min-w-[980px] grid-cols-[72px_140px_minmax(140px,1.2fr)_minmax(140px,1.1fr)_minmax(180px,1.4fr)_90px_110px] items-center gap-3 whitespace-nowrap text-sm text-black dark:text-white">
                          <span
                            className="shrink-0 font-semibold text-primary"
                            title={getCommitSha(commit)}
                          >
                            {getShortCommitSha(commit)}
                          </span>
                          <span
                            className="shrink-0 text-slate-500 dark:text-slate-300"
                            title={formatCommitTimestamp(commit)}
                          >
                            {formatCommitTimestamp(commit)}
                          </span>
                          <span
                            className="truncate"
                            title={getCommitAuthor(commit)}
                          >
                            {getCommitAuthor(commit)}
                          </span>
                          <span
                            className="truncate"
                            title={getCommitRepo(commit)}
                          >
                            {getCommitRepo(commit)}
                          </span>
                          <span
                            className="truncate"
                            title={getCommitProjects(commit)}
                          >
                            {getCommitProjects(commit)}
                          </span>
                          <span className="shrink-0 text-right">
                            REV {formatNumber(getCommitRev(commit))}
                          </span>
                          <span className="shrink-0 text-right">
                            ${formatNumber(getCommitValue(commit))}
                          </span>
                        </div>
                      </div>
                      <p
                        className="mt-2 truncate text-sm text-slate-600 dark:text-slate-300"
                        title={commit.commit_message || '-'}
                      >
                        {commit.commit_message || '-'}
                      </p>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-stroke px-4 py-4 dark:border-strokedark sm:px-6">
                      <div className="mb-3">
                        <p className="mb-1 text-xs font-semibold uppercase text-slate-500">
                          Diff Detail Summary
                        </p>
                        <div className="min-h-24 whitespace-pre-wrap rounded-lg border border-stroke bg-slate-50 p-3 text-sm text-slate-700 dark:border-strokedark dark:bg-meta-4 dark:text-slate-200">
                          {commit.diff_details || '-'}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {hashtags.length ? (
                          hashtags.map(tag => (
                            <span
                              key={tag}
                              className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">
                            No category tags
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {!loading && !commits.length && !error && (
          <div className="rounded-xl border border-stroke bg-white px-4 py-3 text-sm text-slate-500 shadow-default dark:border-strokedark dark:bg-boxdark dark:text-slate-300">
            No commits found for current filters.
          </div>
        )}

        {isLoadingMore && <Loading type="bar" />}

        {!loading && hasMore && <div ref={sentinelRef} className="h-1" />}

        {!loading && !hasMore && commits.length > 0 && (
          <div className="px-2 py-3 text-center text-xs text-slate-400">
            You&apos;ve reached the end of the commit list.
          </div>
        )}
      </div>
    </>
  );
};

export default UserDetail;
